import type {
  CreateCourseRequest,
  CreateCourseResponse,
  CourseModuleDetail,
  CourseModuleLessonDetail,
  CourseModuleLessonQuizConfigDetail,
  ModuleLessonQuizConfigRequest,
} from '@e-learning/core';
import {
  CreateCourseHeader,
  CreateCourseSidebar,
  CreateCourseStepOne,
  CreateCourseStepPagination,
  CreateCourseStepThree,
  CreateCourseStepTwo,
  iconArrowLeftHeaderCourses,
  LoadingModal,
  Toast,
  useTheme,
  validateStepOne,
  validateStepTwo,
  type BasicInfoFormData,
  type CourseLesson,
  type CourseModule,
  type TargetingGroup,
  type TargetingUser,
} from '@e-learning/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuestions } from '../QuestionBank/hooks';
import { useCourses } from './hooks';

interface CourseCategoryItem {
  id: string;
  translations: { languageCode: string; name: string }[];
}

interface CourseStatusItem {
  id: number;
  name: string;
}

type LessonDisplayOrderItem = { id: string; displayOrder: number };

/** A API exige que cada PATCH de display-order de lições contenha apenas lições do mesmo módulo. */
async function updateCourseModuleLessonsDisplayOrderBatches(
  batches: LessonDisplayOrderItem[][],
  updateCourseModuleLessonsDisplayOrder: (items: LessonDisplayOrderItem[]) => Promise<unknown>
): Promise<void> {
  for (const batch of batches) {
    if (batch.length > 0) {
      await updateCourseModuleLessonsDisplayOrder(batch);
    }
  }
}

/** Resposta de GET v1/Course/{id} (detalhes do curso para edição Step 1 + audience) */
interface CourseDetailResponse {
  id: string;
  translations?: { languageCode: string; title?: string; description?: string }[];
  courseCategoryId?: string;
  tags?: string;
  coverImageUrl?: string | null;
  duration?: number;
  effectiveDate?: string | null;
  audienceUsers?: { userId: string; name: string }[];
  audienceUserGroups?: { userGroupId: string; name: string }[];
}

/** Mapeia dados do Step 1 para o body de POST v1/Course. */
function basicInfoToCreateCourseRequest(
  data: BasicInfoFormData
): CreateCourseRequest {
  const translations = [
    {
      languageCode: 'en-US',
      title: data.title.en || '',
      description: data.description.en || '',
    },
    {
      languageCode: 'es-ES',
      title: data.title.es || '',
      description: data.description.es || '',
    },
  ].filter(t => t.title.trim() || t.description.trim());
  if (translations.length === 0) {
    translations.push({
      languageCode: 'en-US',
      title: '',
      description: '',
    });
  }
  const duration = parseFloat(data.durationHours) || 0;
  const trimmedDate = data.effectiveDate?.trim() ?? '';
  const effectiveDate: string | null = trimmedDate
    ? trimmedDate.includes('T')
      ? trimmedDate
      : `${trimmedDate}T00:00:00.000Z`
    : null;
  return {
    translations,
    courseCategoryId: data.categoryId,
    tags: data.tags ?? '',
    duration,
    effectiveDate,
    difficultyLevel: 0,
  };
}

function getCourseIdFromResponse(res: CreateCourseResponse): string | null {
  if (typeof res === 'string' && res.trim()) return res.trim();
  if (res && typeof res === 'object' && 'id' in res) {
    const id = (res as { id?: string }).id;
    if (typeof id === 'string' && id.trim()) return id.trim();
  }
  return null;
}

/** Mapeia audience do GET v1/Course/{id} para initialGroups/initialUsers do Step 3. */
function courseDetailToTargeting(detail: CourseDetailResponse): {
  users: TargetingUser[];
  groups: TargetingGroup[];
} {
  const users: TargetingUser[] = (detail.audienceUsers ?? []).map(u => ({
    id: u.userId,
    name: u.name,
    role: '',
    location: '',
    selected: true,
  }));
  const groups: TargetingGroup[] = (detail.audienceUserGroups ?? []).map(g => ({
    id: g.userGroupId,
    name: g.name,
    type: 'branch',
    members: 0,
    selected: true,
  }));
  return { users, groups };
}

/** Mapeia resposta GET v1/Course/{id} para BasicInfoFormData (Step 1). */
function courseDetailToBasicInfoFormData(detail: CourseDetailResponse): BasicInfoFormData {
  const en = detail.translations?.find((trans) => trans.languageCode === 'en-US');
  const es = detail.translations?.find((trans) => trans.languageCode === 'es-ES');
  const effectiveDateOnly = detail.effectiveDate?.slice(0, 10) ?? '';
  return {
    title: { en: en?.title ?? '', es: es?.title ?? '' },
    description: { en: en?.description ?? '', es: es?.description ?? '' },
    categoryId: detail.courseCategoryId ?? '',
    tags: detail.tags ?? '',
    coverFile: null,
    coverImageUrl: detail.coverImageUrl ?? undefined,
    durationHours: detail.duration != null ? String(detail.duration) : '',
    effectiveDate: effectiveDateOnly,
  };
}

const LESSON_TYPE_MAP: Record<number, CourseLesson['type']> = {
  1: 'video',
  2: 'file',
  3: 'quiz',
};

/** Retorna apenas traduções com title preenchido; se todas vazias, retorna um item com defaultTitle. */
function lessonTranslationsFiltered(
  en: string,
  es: string,
  defaultTitle = 'Lesson'
): { languageCode: string; title: string }[] {
  const arr = [
    { languageCode: 'en-US', title: (en ?? '').trim() },
    { languageCode: 'es-ES', title: (es ?? '').trim() },
  ].filter(tr => tr.title !== '');
  return arr.length > 0 ? arr : [{ languageCode: 'en-US', title: defaultTitle }];
}

/** Mapeia QuizLessonConfig (UI) para o body quizConfig da API. */
function quizConfigToApi(
  config: NonNullable<CourseLesson['quizConfig']>
): ModuleLessonQuizConfigRequest {
  return {
    isManual: config.manualQuestionSelection,
    qtyQuestionLow: config.easyCount,
    qtyQuestionMedium: config.mediumCount,
    qtyQuestionHard: config.hardCount,
    isCourseFinalExam: config.isFinalExam,
  };
}

/** Mapeia lição da API (GET course-module) para CourseLesson (shared). */
function mapLessonDetailToCourseLesson(lesson: CourseModuleLessonDetail): CourseLesson {
  const en = lesson.translations?.find((t: { languageCode: string; title: string }) => t.languageCode === 'en-US');
  const es = lesson.translations?.find((t: { languageCode: string; title: string }) => t.languageCode === 'es-ES');
  const lessonType = LESSON_TYPE_MAP[lesson.type] ?? 'video';
  const mappedFiles =
    lessonType === 'file'
      ? (lesson.courseModuleLessonFiles ?? [])
          .map((file: unknown) => {
            if (!file || typeof file !== 'object' || !('id' in file)) return null;
            const raw = file as {
              id: unknown;
              fileName?: unknown;
              name?: unknown;
              originalName?: unknown;
              originalFileName?: unknown;
            };
            const id = typeof raw.id === 'string' ? raw.id : '';
            if (!id) return null;
            const name =
              (typeof raw.fileName === 'string' && raw.fileName) ||
              (typeof raw.name === 'string' && raw.name) ||
              (typeof raw.originalFileName === 'string' && raw.originalFileName) ||
              (typeof raw.originalName === 'string' && raw.originalName) ||
              id;
            return { id, name };
          })
          .filter((f): f is { id: string; name: string } => Boolean(f))
      : [];
  const rawQuiz = lesson.courseModuleLessonQuizConfig as CourseModuleLessonQuizConfigDetail | ModuleLessonQuizConfigRequest | null | undefined;
  const quizConfig =
    lessonType === 'quiz' && rawQuiz
      ? {
          manualQuestionSelection: rawQuiz.isManual,
          easyCount: rawQuiz.qtyQuestionLow,
          mediumCount: rawQuiz.qtyQuestionMedium,
          hardCount: rawQuiz.qtyQuestionHard,
          isFinalExam: rawQuiz.isCourseFinalExam,
          ...('courseModuleLessonQuestions' in rawQuiz && rawQuiz.courseModuleLessonQuestions?.length
            ? {
                selectedQuestionIds: rawQuiz.courseModuleLessonQuestions
                  .map((q: { questionId?: string }) => q.questionId)
                  .filter((id: string | undefined): id is string => Boolean(id)),
              }
            : {}),
        }
      : undefined;
  const quizConfigId =
    lessonType === 'quiz' && rawQuiz && typeof rawQuiz === 'object' && 'id' in rawQuiz && rawQuiz.id
      ? (rawQuiz as CourseModuleLessonQuizConfigDetail).id
      : undefined;
  return {
    id: lesson.id,
    type: lessonType,
    xp: lesson.points ?? 0,
    name: { en: en?.title ?? '', es: es?.title ?? '' },
    ...(lessonType === 'video' && lesson.videoUrl && { videoUrl: lesson.videoUrl }),
    ...(mappedFiles.length > 0 && { courseModuleLessonFiles: mappedFiles }),
    ...(mappedFiles.length > 0 && { originalCourseModuleLessonFiles: mappedFiles }),
    ...(quizConfigId && { courseModuleLessonQuizConfigId: quizConfigId }),
    ...(quizConfig && { quizConfig }),
  };
}

/** Mapeia módulos da API (GET course-module) para CourseModule[] (shared). */
function mapCourseModuleDetailsToModules(details: CourseModuleDetail[]): CourseModule[] {
  return details.map((mod: CourseModuleDetail) => {
    const en = mod.translations?.find((t: { languageCode: string; title: string }) => t.languageCode === 'en-US');
    const es = mod.translations?.find((t: { languageCode: string; title: string }) => t.languageCode === 'es-ES');
    return {
      id: mod.id,
      name: { en: en?.title ?? '', es: es?.title ?? '' },
      lessons: (mod.courseModuleLessons ?? []).map(mapLessonDetailToCourseLesson),
    };
  });
}

export default function CreateCoursePage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { courseId: courseIdParam } = useParams<{ courseId: string }>();
  const isEditMode = Boolean(courseIdParam);
  const {
    fetchCourseCategories,
    fetchCourseStatus,
    fetchCourseById,
    fetchCourseModules,
    fetchModuleLesson,
    createCourse,
    updateCourse,
    uploadCourseCover,
    createModule,
    updateModule,
    createModuleLesson,
    updateModuleLesson,
    updateCourseModulesDisplayOrder,
    updateCourseModuleLessonsDisplayOrder,
    deleteCourseModuleLessonFile,
    uploadCourseModuleLessonFile,
    updateQuizConfigQuestions,
    fetchTargetingGroups,
    fetchTargetingUsers,
    updateCourseAudience,
    updateCourseStatus,
  } = useCourses();
  const { fetchQuestions: fetchQuestionsApi } = useQuestions();
  const [categoryList, setCategoryList] = useState<CourseCategoryItem[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [step3ReachedViaNext, setStep3ReachedViaNext] = useState(false);
  const [draftSavedOnStep3, setDraftSavedOnStep3] = useState(false);
  const [createdCourseId, setCreatedCourseId] = useState<string | null>(courseIdParam ?? null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setter reservado para uso futuro
  const [step1Submitting, setStep1Submitting] = useState(false);
  const [step1Error, setStep1Error] = useState<string | null>(null);
  const [step1LoadingEdit, setStep1LoadingEdit] = useState(isEditMode);
  const [saveDraftSubmitting, setSaveDraftSubmitting] = useState(false);
  const [publishSubmitting, setPublishSubmitting] = useState(false);
  /** Mensagem de erro do save draft exibida no Toast (modal padrão do sistema) */
  const [saveDraftToastError, setSaveDraftToastError] = useState<string | null>(null);

  useEffect(() => {
    if (!saveDraftToastError) return;
    const id = setTimeout(() => setSaveDraftToastError(null), 4000);
    return () => clearTimeout(id);
  }, [saveDraftToastError]);
  const completionPercent =
    currentStep === 1
      ? 0
      : currentStep === 2
        ? 33
        : currentStep === 3 && draftSavedOnStep3
          ? 100
          : 67;
  const [basicInfoData, setBasicInfoData] = useState<BasicInfoFormData | null>(
    null
  );

  const fetchQuestionsForQuiz = useCallback(
    async (params: {
      page: number;
      limit: number;
      includeInactive?: boolean;
      title?: string;
    }) => {
      return fetchQuestionsApi({
        page: params.page,
        limit: params.limit,
        includeInactive: params.includeInactive ?? false,
        title: params.title,
        courseCategoryId: basicInfoData?.categoryId?.trim() || undefined,
        courseId: (courseIdParam ?? createdCourseId ?? '').trim() || undefined,
      });
    },
    [fetchQuestionsApi, basicInfoData?.categoryId, courseIdParam, createdCourseId]
  );

  const [modulesData, setModulesData] = useState<CourseModule[]>([]);
  /** Snapshot dos módulos carregados na edição (usado para detectar remoções no Step 2). */
  const [originalModulesFromEdit, setOriginalModulesFromEdit] = useState<CourseModule[]>([]);
  const [modulesLoadedFromEdit, setModulesLoadedFromEdit] = useState(false);
  const [targetingSelection, setTargetingSelection] = useState<{
    userIds: string[];
    groupIds: string[];
  }>({ userIds: [], groupIds: [] });
  /** Audience carregado na edição (GET Course/{id}) para preencher o Step 3. */
  const [initialTargetingFromEdit, setInitialTargetingFromEdit] = useState<{
    users: TargetingUser[];
    groups: TargetingGroup[];
  }>({ users: [], groups: [] });
  const [audienceLoadedFromEdit, setAudienceLoadedFromEdit] = useState(false);

  const categoryOptions = useMemo(
    () =>
      categoryList.map(c => ({
        value: c.id,
        label: c.translations?.[0]?.name ?? c.id,
      })),
    [categoryList]
  );

  const step1Validation = useMemo(
    () => validateStepOne(basicInfoData ?? null),
    [basicInfoData]
  );
  const isStep1Valid = step1Validation.valid;

  const step2Validation = useMemo(
    () => validateStepTwo(modulesData),
    [modulesData]
  );
  const isStep2Valid = step2Validation.valid;

  useEffect(() => {
    let cancelled = false;
    fetchCourseCategories({ includeInactive: false })
      .then((list: CourseCategoryItem[]) => {
        if (!cancelled) setCategoryList(list);
      })
      .catch(() => {
        if (!cancelled) setCategoryList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchCourseCategories]);

  useEffect(() => {
    if (!isEditMode || !courseIdParam) return;
    let cancelled = false;
    setStep1LoadingEdit(true);
    fetchCourseById(courseIdParam)
      .then((detail: CourseDetailResponse) => {
        if (!cancelled) {
          setBasicInfoData(courseDetailToBasicInfoFormData(detail));
          const targeting = courseDetailToTargeting(detail);
          setInitialTargetingFromEdit(targeting);
          setTargetingSelection({
            userIds: targeting.users.map(u => u.id),
            groupIds: targeting.groups.map(g => g.id),
          });
          setAudienceLoadedFromEdit(true);
        }
      })
      .catch(() => {
        if (!cancelled) setStep1Error(t('courses.create.error_loading_course') ?? 'Erro ao carregar o curso.');
      })
      .finally(() => {
        if (!cancelled) setStep1LoadingEdit(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEditMode, courseIdParam, fetchCourseById, t]);

  useEffect(() => {
    if (!isEditMode || !courseIdParam) return;
    let cancelled = false;
    setModulesLoadedFromEdit(false);
    fetchCourseModules(courseIdParam, { includeInactive: false })
      .then((details: CourseModuleDetail[]) => {
        if (cancelled) return;
        const mapped = mapCourseModuleDetailsToModules(details);
        setModulesData(mapped);
        setOriginalModulesFromEdit(mapped);
      })
      .catch(() => {
        if (cancelled) return;
        setModulesData([]);
        setOriginalModulesFromEdit([]);
      })
      .finally(() => {
        if (!cancelled) setModulesLoadedFromEdit(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isEditMode, courseIdParam, fetchCourseModules]);

  const handleSaveDraft = useCallback(async (
    options: { navigateAfterSave?: boolean } = {}
  ): Promise<string | null> => {
    const { navigateAfterSave = true } = options;
    // No modo "novo", o rascunho só deve ser salvo no Step 3.
    // No modo "edição", o botão deve existir em todos os steps, então permitimos salvar também fora do Step 3.
    if (!isEditMode && currentStep !== 3) return null;
    if (!basicInfoData) {
      navigate('/admin/courses');
      return null;
    }
    setSaveDraftToastError(null);
    setSaveDraftSubmitting(true);
    const errMsg =
      t('courses.create.error_creating_course') || 'Erro ao criar curso. Tente novamente.';
    const errMsgUpdate =
      t('courses.create.error_updating_course') || 'Erro ao atualizar curso. Tente novamente.';
    try {
      const body = basicInfoToCreateCourseRequest(basicInfoData);

      if (isEditMode && createdCourseId) {
        await updateCourse(createdCourseId, body);
        if (basicInfoData.coverFile) {
          await uploadCourseCover(createdCourseId, basicInfoData.coverFile);
        }
        const lessonTypeMap = { video: 1 as const, file: 2 as const, quiz: 3 as const };
        const defaultLessonTitles: Record<string, { en: string; es: string }> = {
          video: { en: 'Video', es: 'Vídeo' },
          file: { en: 'File', es: 'Archivo' },
          quiz: { en: 'Quiz', es: 'Cuestionario' },
        };
        const courseId = createdCourseId;
        const moduleOrder: { id: string; displayOrder: number }[] = [];
        const lessonOrderBatches: LessonDisplayOrderItem[][] = [];

        for (let i = 0; i < modulesData.length; i++) {
          const mod = modulesData[i];
          let lessonIndex = 0;
          const moduleLessonOrder: LessonDisplayOrderItem[] = [];

          if (mod.id && !mod.id.startsWith('id-')) {
            // Módulo existente: atualizar e depois processar lições (PUT existentes, POST novas)
            moduleOrder.push({ id: mod.id, displayOrder: i });
            const moduleTranslations = [
              { languageCode: 'en-US' as const, title: mod.name?.en ?? '' },
              { languageCode: 'es-ES' as const, title: mod.name?.es ?? '' },
            ].filter(tr => tr.title.trim());
            await updateModule(mod.id, {
              translations:
                moduleTranslations.length > 0
                  ? moduleTranslations
                  : [{ languageCode: 'en-US', title: 'Module' }],
              isActive: true,
              displayOrder: i,
            });

            for (const lesson of mod.lessons) {
              if (lesson.id && !lesson.id.startsWith('id-')) {
                // Lição existente: PUT
                moduleLessonOrder.push({ id: lesson.id, displayOrder: lessonIndex++ });
                const apiType = lessonTypeMap[lesson.type];
                const translations = lessonTranslationsFiltered(
                  lesson.name?.en ?? '',
                  lesson.name?.es ?? '',
                  'Lesson'
                );
                await updateModuleLesson(lesson.id, {
                  type: apiType,
                  ...(lesson.type === 'video' && lesson.videoUrl && { videoUrl: lesson.videoUrl }),
                  ...(lesson.type === 'quiz' && lesson.quizConfig && { quizConfig: quizConfigToApi(lesson.quizConfig) }),
                  points: lesson.xp ?? 0,
                  translations,
                });
                if (
                  lesson.type === 'quiz' &&
                  lesson.quizConfig?.manualQuestionSelection &&
                  (lesson.quizConfig.selectedQuestionIds?.length ?? 0) > 0 &&
                  lesson.courseModuleLessonQuizConfigId
                ) {
                  await updateQuizConfigQuestions(
                    lesson.courseModuleLessonQuizConfigId,
                    lesson.quizConfig.selectedQuestionIds ?? []
                  );
                }
              } else {
                // Lição nova (id-...): POST createModuleLesson
                const apiType = lessonTypeMap[lesson.type];
                const titles = defaultLessonTitles[lesson.type] ?? { en: 'Lesson', es: 'Lección' };
                const createdLesson = await createModuleLesson(mod.id, {
                  type: apiType,
                  ...(lesson.type === 'video' && lesson.videoUrl && { videoUrl: lesson.videoUrl }),
                  ...(lesson.type === 'quiz' && lesson.quizConfig && { quizConfig: quizConfigToApi(lesson.quizConfig) }),
                  points: lesson.xp ?? 10,
                  translations: lessonTranslationsFiltered(
                    lesson.name?.en ?? titles.en,
                    lesson.name?.es ?? titles.es,
                    titles.en
                  ),
                });
                moduleLessonOrder.push({ id: createdLesson.id, displayOrder: lessonIndex++ });
                if (lesson.type === 'file' && (lesson.files?.length ?? 0) > 0) {
                  for (const file of lesson.files ?? []) {
                    await uploadCourseModuleLessonFile(createdLesson.id, file);
                  }
                }
                if (
                  lesson.type === 'quiz' &&
                  lesson.quizConfig?.manualQuestionSelection &&
                  (lesson.quizConfig.selectedQuestionIds?.length ?? 0) > 0
                ) {
                  const detail = await fetchModuleLesson(createdLesson.id);
                  const quizConfigId =
                    detail?.courseModuleLessonQuizConfig &&
                    typeof detail.courseModuleLessonQuizConfig === 'object' &&
                    'id' in detail.courseModuleLessonQuizConfig
                      ? (detail.courseModuleLessonQuizConfig as CourseModuleLessonQuizConfigDetail).id
                      : undefined;
                  if (quizConfigId) {
                    await updateQuizConfigQuestions(
                      quizConfigId,
                      lesson.quizConfig.selectedQuestionIds ?? []
                    );
                  }
                }
              }
            }

            // Em edição, sincroniza anexos de lições file existentes:
            // - remove os arquivos excluídos no modal
            // - envia os novos arquivos adicionados no modal
            for (const lesson of mod.lessons) {
              if (lesson.type !== 'file') continue;
              if (lesson.id && !lesson.id.startsWith('id-')) {
                const originalFiles = lesson.originalCourseModuleLessonFiles ?? [];
                const currentFiles = lesson.courseModuleLessonFiles ?? [];
                const currentIds = new Set(currentFiles.map(file => file.id));
                for (const existingFile of originalFiles) {
                  if (!currentIds.has(existingFile.id)) {
                    await deleteCourseModuleLessonFile(existingFile.id);
                  }
                }
                for (const file of lesson.files ?? []) {
                  await uploadCourseModuleLessonFile(lesson.id, file);
                }
              }
            }
          } else {
            // Módulo novo (id-...): POST createModule + createModuleLesson para cada lição
            const moduleTranslations = [
              { languageCode: 'en-US' as const, title: mod.name?.en ?? '' },
              { languageCode: 'es-ES' as const, title: mod.name?.es ?? '' },
            ].filter(tr => tr.title.trim());
            const createdModule = await createModule(courseId, {
              translations:
                moduleTranslations.length > 0
                  ? moduleTranslations
                  : [{ languageCode: 'en-US', title: 'Module' }],
            });
            const courseModuleId = createdModule.id;
            moduleOrder.push({ id: createdModule.id, displayOrder: i });

            for (const lesson of mod.lessons) {
              const apiType = lessonTypeMap[lesson.type];
              const titles = defaultLessonTitles[lesson.type] ?? { en: 'Lesson', es: 'Lección' };
              const createdLesson = await createModuleLesson(courseModuleId, {
                type: apiType,
                ...(lesson.type === 'video' && lesson.videoUrl && { videoUrl: lesson.videoUrl }),
                ...(lesson.type === 'quiz' && lesson.quizConfig && { quizConfig: quizConfigToApi(lesson.quizConfig) }),
                points: lesson.xp ?? 10,
                translations: lessonTranslationsFiltered(
                  lesson.name?.en ?? titles.en,
                  lesson.name?.es ?? titles.es,
                  titles.en
                ),
              });
              moduleLessonOrder.push({ id: createdLesson.id, displayOrder: lessonIndex++ });
              if (lesson.type === 'file' && (lesson.files?.length ?? 0) > 0) {
                for (const file of lesson.files ?? []) {
                  await uploadCourseModuleLessonFile(createdLesson.id, file);
                }
              }
              if (
                lesson.type === 'quiz' &&
                lesson.quizConfig?.manualQuestionSelection &&
                (lesson.quizConfig.selectedQuestionIds?.length ?? 0) > 0
              ) {
                const detail = await fetchModuleLesson(createdLesson.id);
                const quizConfigId =
                  detail?.courseModuleLessonQuizConfig &&
                  typeof detail.courseModuleLessonQuizConfig === 'object' &&
                  'id' in detail.courseModuleLessonQuizConfig
                    ? (detail.courseModuleLessonQuizConfig as CourseModuleLessonQuizConfigDetail).id
                    : undefined;
                if (quizConfigId) {
                  await updateQuizConfigQuestions(
                    quizConfigId,
                    lesson.quizConfig.selectedQuestionIds ?? []
                  );
                }
              }
            }
          }
          if (moduleLessonOrder.length > 0) {
            lessonOrderBatches.push(moduleLessonOrder);
          }
        }

        // No modo edição, se o usuário removeu módulos no Step 2, precisamos "desativar" no backend
        // para evitar que a persistência de display-order deixe módulos antigos reaparecerem/duplicarem.
        const activeModuleIds = new Set(modulesData.map(m => m.id));
        const deletedModulesFromEdit =
          originalModulesFromEdit.length > 0
            ? originalModulesFromEdit.filter(om => !activeModuleIds.has(om.id))
            : [];

        if (deletedModulesFromEdit.length > 0) {
          try {
            const deletedModuleLessonIds = new Set<string>();
            for (const deleted of deletedModulesFromEdit) {
              let deletedLessonIndex = 0;
              const deletedModuleLessonOrder: LessonDisplayOrderItem[] = [];
              const deletedDisplayOrder = originalModulesFromEdit.findIndex(m => m.id === deleted.id);

              const moduleTranslations = [
                { languageCode: 'en-US' as const, title: deleted.name?.en ?? '' },
                { languageCode: 'es-ES' as const, title: deleted.name?.es ?? '' },
              ].filter(tr => tr.title.trim());

              await updateModule(deleted.id, {
                translations:
                  moduleTranslations.length > 0
                    ? moduleTranslations
                    : [{ languageCode: 'en-US', title: 'Module' }],
                isActive: false,
                displayOrder: deletedDisplayOrder >= 0 ? deletedDisplayOrder : moduleOrder.length,
              });

              moduleOrder.push({
                id: deleted.id,
                displayOrder: deletedDisplayOrder >= 0 ? deletedDisplayOrder : moduleOrder.length,
              });

              for (const lesson of deleted.lessons) {
                if (!lesson.id || lesson.id.startsWith('id-')) continue;
                if (deletedModuleLessonIds.has(lesson.id)) continue;
                deletedModuleLessonIds.add(lesson.id);
                deletedModuleLessonOrder.push({
                  id: lesson.id,
                  displayOrder: deletedLessonIndex++,
                });
              }
              if (deletedModuleLessonOrder.length > 0) {
                lessonOrderBatches.push(deletedModuleLessonOrder);
              }
            }
          } catch {
            // Não bloquear salvar rascunho se desativar falhar.
          }
        }

        try {
          if (moduleOrder.length > 0) {
            await updateCourseModulesDisplayOrder(moduleOrder);
          }
          await updateCourseModuleLessonsDisplayOrderBatches(
            lessonOrderBatches,
            updateCourseModuleLessonsDisplayOrder
          );
        } catch {
          // falha em ordenação não impede salvar rascunho
        }

        try {
          await updateCourseAudience(courseId, {
            userIds: targetingSelection.userIds,
            userGroupIds: targetingSelection.groupIds,
          });
        } catch {
          // falha em audience não impede salvar rascunho
        }
        setDraftSavedOnStep3(true);
        if (navigateAfterSave) navigate('/admin/courses');
        return courseId;
      }

      // Novo curso: se já houve POST bem-sucedido anteriormente (createdCourseId),
      // retries de Save Draft devem seguir como atualização (PUT).
      if (!isEditMode && createdCourseId) {
        await updateCourse(createdCourseId, body);
        if (basicInfoData.coverFile) {
          await uploadCourseCover(createdCourseId, basicInfoData.coverFile);
        }
        const lessonTypeMap = { video: 1 as const, file: 2 as const, quiz: 3 as const };
        const defaultLessonTitles: Record<string, { en: string; es: string }> = {
          video: { en: 'Video', es: 'Vídeo' },
          file: { en: 'File', es: 'Archivo' },
          quiz: { en: 'Quiz', es: 'Cuestionario' },
        };
        const courseId = createdCourseId;
        const persistedModules = await fetchCourseModules(courseId, {
          includeInactive: false,
        })
          .then((details: CourseModuleDetail[]) => mapCourseModuleDetailsToModules(details))
          .catch(() => []);
        const modulesToPersist = modulesData.map((mod, modIndex) => {
          const persistedModule = persistedModules[modIndex];
          const resolvedModuleId =
            mod.id && !mod.id.startsWith('id-') ? mod.id : persistedModule?.id;
          const persistedLessons = persistedModule?.lessons ?? [];
          const resolvedLessons = mod.lessons.map((lesson, lessonIndex) => {
            const persistedLesson = persistedLessons[lessonIndex];
            const hasRealId = Boolean(lesson.id && !lesson.id.startsWith('id-'));
            const canReusePersistedId =
              !hasRealId &&
              Boolean(persistedLesson) &&
              persistedLesson.type === lesson.type;
            if (!canReusePersistedId || !persistedLesson) return lesson;
            return {
              ...lesson,
              id: persistedLesson.id,
              courseModuleLessonQuizConfigId:
                lesson.courseModuleLessonQuizConfigId ??
                persistedLesson.courseModuleLessonQuizConfigId,
              courseModuleLessonFiles:
                lesson.courseModuleLessonFiles ??
                persistedLesson.courseModuleLessonFiles,
              originalCourseModuleLessonFiles:
                lesson.originalCourseModuleLessonFiles ??
                persistedLesson.originalCourseModuleLessonFiles,
            };
          });
          return {
            ...mod,
            ...(resolvedModuleId ? { id: resolvedModuleId } : {}),
            lessons: resolvedLessons,
          };
        });
        const moduleOrder: { id: string; displayOrder: number }[] = [];
        const lessonOrderBatches: LessonDisplayOrderItem[][] = [];
        for (let i = 0; i < modulesToPersist.length; i++) {
          const mod = modulesToPersist[i];
          let lessonIndex = 0;
          const moduleLessonOrder: LessonDisplayOrderItem[] = [];
          if (mod.id && !mod.id.startsWith('id-')) {
            moduleOrder.push({ id: mod.id, displayOrder: i });
            const moduleTranslations = [
              { languageCode: 'en-US' as const, title: mod.name?.en ?? '' },
              { languageCode: 'es-ES' as const, title: mod.name?.es ?? '' },
            ].filter(tr => tr.title.trim());
            await updateModule(mod.id, {
              translations:
                moduleTranslations.length > 0
                  ? moduleTranslations
                  : [{ languageCode: 'en-US', title: 'Module' }],
              isActive: true,
              displayOrder: i,
            });

            for (const lesson of mod.lessons) {
              if (lesson.id && !lesson.id.startsWith('id-')) {
                moduleLessonOrder.push({ id: lesson.id, displayOrder: lessonIndex++ });
                const apiType = lessonTypeMap[lesson.type];
                const translations = lessonTranslationsFiltered(
                  lesson.name?.en ?? '',
                  lesson.name?.es ?? '',
                  'Lesson'
                );
                await updateModuleLesson(lesson.id, {
                  type: apiType,
                  ...(lesson.type === 'video' && lesson.videoUrl && { videoUrl: lesson.videoUrl }),
                  ...(lesson.type === 'quiz' && lesson.quizConfig && { quizConfig: quizConfigToApi(lesson.quizConfig) }),
                  points: lesson.xp ?? 0,
                  translations,
                });
                if (
                  lesson.type === 'quiz' &&
                  lesson.quizConfig?.manualQuestionSelection &&
                  (lesson.quizConfig.selectedQuestionIds?.length ?? 0) > 0 &&
                  lesson.courseModuleLessonQuizConfigId
                ) {
                  await updateQuizConfigQuestions(
                    lesson.courseModuleLessonQuizConfigId,
                    lesson.quizConfig.selectedQuestionIds ?? []
                  );
                }
              } else {
                const apiType = lessonTypeMap[lesson.type];
                const titles = defaultLessonTitles[lesson.type] ?? { en: 'Lesson', es: 'Lección' };
                const createdLesson = await createModuleLesson(mod.id, {
                  type: apiType,
                  ...(lesson.type === 'video' && lesson.videoUrl && { videoUrl: lesson.videoUrl }),
                  ...(lesson.type === 'quiz' && lesson.quizConfig && { quizConfig: quizConfigToApi(lesson.quizConfig) }),
                  points: lesson.xp ?? 10,
                  translations: lessonTranslationsFiltered(
                    lesson.name?.en ?? titles.en,
                    lesson.name?.es ?? titles.es,
                    titles.en
                  ),
                });
                moduleLessonOrder.push({ id: createdLesson.id, displayOrder: lessonIndex++ });
                if (lesson.type === 'file' && (lesson.files?.length ?? 0) > 0) {
                  for (const file of lesson.files ?? []) {
                    await uploadCourseModuleLessonFile(createdLesson.id, file);
                  }
                }
                if (
                  lesson.type === 'quiz' &&
                  lesson.quizConfig?.manualQuestionSelection &&
                  (lesson.quizConfig.selectedQuestionIds?.length ?? 0) > 0
                ) {
                  const detail = await fetchModuleLesson(createdLesson.id);
                  const quizConfigId =
                    detail?.courseModuleLessonQuizConfig &&
                    typeof detail.courseModuleLessonQuizConfig === 'object' &&
                    'id' in detail.courseModuleLessonQuizConfig
                      ? (detail.courseModuleLessonQuizConfig as CourseModuleLessonQuizConfigDetail).id
                      : undefined;
                  if (quizConfigId) {
                    await updateQuizConfigQuestions(
                      quizConfigId,
                      lesson.quizConfig.selectedQuestionIds ?? []
                    );
                  }
                }
              }
            }

            for (const lesson of mod.lessons) {
              if (lesson.type !== 'file') continue;
              if (lesson.id && !lesson.id.startsWith('id-')) {
                const originalFiles = lesson.originalCourseModuleLessonFiles ?? [];
                const currentFiles = lesson.courseModuleLessonFiles ?? [];
                const currentIds = new Set(currentFiles.map(file => file.id));
                for (const existingFile of originalFiles) {
                  if (!currentIds.has(existingFile.id)) {
                    await deleteCourseModuleLessonFile(existingFile.id);
                  }
                }
                for (const file of lesson.files ?? []) {
                  await uploadCourseModuleLessonFile(lesson.id, file);
                }
              }
            }
          } else {
            const moduleTranslations = [
              { languageCode: 'en-US' as const, title: mod.name?.en ?? '' },
              { languageCode: 'es-ES' as const, title: mod.name?.es ?? '' },
            ].filter(tr => tr.title.trim());
            const createdModule = await createModule(courseId, {
              translations:
                moduleTranslations.length > 0
                  ? moduleTranslations
                  : [{ languageCode: 'en-US', title: 'Module' }],
            });
            const courseModuleId = createdModule.id;
            moduleOrder.push({ id: createdModule.id, displayOrder: i });

            for (const lesson of mod.lessons) {
              const apiType = lessonTypeMap[lesson.type];
              const titles = defaultLessonTitles[lesson.type] ?? { en: 'Lesson', es: 'Lección' };
              const createdLesson = await createModuleLesson(courseModuleId, {
                type: apiType,
                ...(lesson.type === 'video' && lesson.videoUrl && { videoUrl: lesson.videoUrl }),
                ...(lesson.type === 'quiz' && lesson.quizConfig && { quizConfig: quizConfigToApi(lesson.quizConfig) }),
                points: lesson.xp ?? 10,
                translations: lessonTranslationsFiltered(
                  lesson.name?.en ?? titles.en,
                  lesson.name?.es ?? titles.es,
                  titles.en
                ),
              });
              moduleLessonOrder.push({ id: createdLesson.id, displayOrder: lessonIndex++ });
              if (lesson.type === 'file' && (lesson.files?.length ?? 0) > 0) {
                for (const file of lesson.files ?? []) {
                  await uploadCourseModuleLessonFile(createdLesson.id, file);
                }
              }
              if (
                lesson.type === 'quiz' &&
                lesson.quizConfig?.manualQuestionSelection &&
                (lesson.quizConfig.selectedQuestionIds?.length ?? 0) > 0
              ) {
                const detail = await fetchModuleLesson(createdLesson.id);
                const quizConfigId =
                  detail?.courseModuleLessonQuizConfig &&
                  typeof detail.courseModuleLessonQuizConfig === 'object' &&
                  'id' in detail.courseModuleLessonQuizConfig
                    ? (detail.courseModuleLessonQuizConfig as CourseModuleLessonQuizConfigDetail).id
                    : undefined;
                if (quizConfigId) {
                  await updateQuizConfigQuestions(
                    quizConfigId,
                    lesson.quizConfig.selectedQuestionIds ?? []
                  );
                }
              }
            }
          }
          if (moduleLessonOrder.length > 0) {
            lessonOrderBatches.push(moduleLessonOrder);
          }
        }
        try {
          if (moduleOrder.length > 0) {
            await updateCourseModulesDisplayOrder(moduleOrder);
          }
          await updateCourseModuleLessonsDisplayOrderBatches(
            lessonOrderBatches,
            updateCourseModuleLessonsDisplayOrder
          );
        } catch {
          // falha em ordenação não impede salvar rascunho
        }
        try {
          await updateCourseAudience(courseId, {
            userIds: targetingSelection.userIds,
            userGroupIds: targetingSelection.groupIds,
          });
        } catch {
          // falha em audience não impede salvar rascunho
        }
        setDraftSavedOnStep3(true);
        if (navigateAfterSave) navigate('/admin/courses');
        return courseId;
      }

      const res = await createCourse(body);
      const courseId = getCourseIdFromResponse(res);
      if (!courseId) {
        setSaveDraftToastError(t('courses.create.error_creating_course') || 'Não foi possível obter o ID do curso. Tente novamente.');
        return null;
      }
      setCreatedCourseId(courseId);
      if (basicInfoData.coverFile) {
        await uploadCourseCover(courseId, basicInfoData.coverFile);
      }
      const lessonTypeMap = { video: 1 as const, file: 2 as const, quiz: 3 as const };
      const defaultLessonTitles: Record<string, { en: string; es: string }> = {
        video: { en: 'Video', es: 'Vídeo' },
        file: { en: 'File', es: 'Archivo' },
        quiz: { en: 'Quiz', es: 'Cuestionario' },
      };
      const moduleOrder: { id: string; displayOrder: number }[] = [];
      const lessonOrderBatches: LessonDisplayOrderItem[][] = [];
      for (let i = 0; i < modulesData.length; i++) {
        const mod = modulesData[i];
        let lessonIndex = 0;
        const moduleLessonOrder: LessonDisplayOrderItem[] = [];
        const moduleTranslations = [
          { languageCode: 'en-US', title: mod.name?.en ?? '' },
          { languageCode: 'es-ES', title: mod.name?.es ?? '' },
        ].filter(tr => tr.title.trim());
        const createdModule = await createModule(courseId, {
          translations: moduleTranslations.length > 0 ? moduleTranslations : [{ languageCode: 'en-US', title: 'Module' }],
        });
        const courseModuleId = createdModule.id;
        moduleOrder.push({ id: createdModule.id, displayOrder: i });
        for (const lesson of mod.lessons) {
          const apiType = lessonTypeMap[lesson.type];
          const titles = defaultLessonTitles[lesson.type] ?? { en: 'Lesson', es: 'Lección' };
          const createdLesson = await createModuleLesson(courseModuleId, {
            type: apiType,
            ...(lesson.type === 'video' && lesson.videoUrl && { videoUrl: lesson.videoUrl }),
            ...(lesson.type === 'quiz' && lesson.quizConfig && { quizConfig: quizConfigToApi(lesson.quizConfig) }),
            points: lesson.xp ?? 10,
            translations: lessonTranslationsFiltered(
              lesson.name?.en ?? titles.en,
              lesson.name?.es ?? titles.es,
              titles.en
            ),
          });
          moduleLessonOrder.push({ id: createdLesson.id, displayOrder: lessonIndex++ });
          if (lesson.type === 'file' && (lesson.files?.length ?? 0) > 0) {
            for (const file of lesson.files ?? []) {
              await uploadCourseModuleLessonFile(createdLesson.id, file);
            }
          }
          if (
            lesson.type === 'quiz' &&
            lesson.quizConfig?.manualQuestionSelection &&
            (lesson.quizConfig.selectedQuestionIds?.length ?? 0) > 0
          ) {
            const detail = await fetchModuleLesson(createdLesson.id);
            const quizConfigId =
              detail?.courseModuleLessonQuizConfig &&
              typeof detail.courseModuleLessonQuizConfig === 'object' &&
              'id' in detail.courseModuleLessonQuizConfig
                ? (detail.courseModuleLessonQuizConfig as CourseModuleLessonQuizConfigDetail).id
                : undefined;
            if (quizConfigId) {
              await updateQuizConfigQuestions(
                quizConfigId,
                lesson.quizConfig.selectedQuestionIds ?? []
              );
            }
          }
        }
        if (moduleLessonOrder.length > 0) {
          lessonOrderBatches.push(moduleLessonOrder);
        }
      }
      try {
        if (moduleOrder.length > 0) {
          await updateCourseModulesDisplayOrder(moduleOrder);
        }
        await updateCourseModuleLessonsDisplayOrderBatches(
          lessonOrderBatches,
          updateCourseModuleLessonsDisplayOrder
        );
      } catch {
        // falha em ordenação não impede salvar rascunho
      }
      try {
        await updateCourseAudience(courseId, {
          userIds: targetingSelection.userIds,
          userGroupIds: targetingSelection.groupIds,
        });
      } catch {
        // falha em audience não impede salvar rascunho
      }
      setDraftSavedOnStep3(true);
      if (navigateAfterSave) navigate('/admin/courses');
      return courseId;
    } catch (err: unknown) {
      const isEdit = isEditMode && !!createdCourseId;
      const responseData = (err as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } })?.response?.data;
      const messages = Array.isArray(responseData?.errors)
        ? responseData.errors.map(e => e.message).filter(Boolean) as string[]
        : [];
      const message =
        messages.length > 0
          ? messages.join(' ')
          : responseData?.message ??
            (err as { message?: string })?.message ??
            (isEdit ? errMsgUpdate : errMsg);
      setSaveDraftToastError(String(message));
      return null;
    } finally {
      setSaveDraftSubmitting(false);
    }
  }, [
    currentStep,
    basicInfoData,
    modulesData,
    targetingSelection,
    isEditMode,
    createdCourseId,
    originalModulesFromEdit,
    createCourse,
    updateCourse,
    updateCourseAudience,
    updateModule,
    updateModuleLesson,
    updateCourseModulesDisplayOrder,
    updateCourseModuleLessonsDisplayOrder,
    uploadCourseCover,
    createModule,
    createModuleLesson,
    deleteCourseModuleLessonFile,
    uploadCourseModuleLessonFile,
    updateQuizConfigQuestions,
    fetchModuleLesson,
    fetchCourseModules,
    navigate,
    t,
  ]);

  const resolvePublishedStatusId = useCallback(async (): Promise<number | null> => {
    try {
      const statusList = await fetchCourseStatus();
      const published = statusList.find((status: CourseStatusItem) => {
        const normalized = status.name.trim().toLowerCase();
        return normalized === 'published' || normalized === 'publicado';
      });
      if (!published) return null;
      const id = Number(published.id);
      return Number.isFinite(id) ? id : null;
    } catch {
      return null;
    }
  }, [fetchCourseStatus]);

  const handlePublish = useCallback(async () => {
    if (currentStep !== 3 || publishSubmitting) return;
    setSaveDraftToastError(null);
    setPublishSubmitting(true);
    try {
      const courseId = await handleSaveDraft({ navigateAfterSave: false });
      if (!courseId) return;
      const nextId = await resolvePublishedStatusId();
      if (!nextId) {
        setSaveDraftToastError(
          t('courses.create.error_publishing_course') ||
            'Erro ao publicar curso. Tente novamente.'
        );
        return;
      }
      await updateCourseStatus(courseId, { status: nextId });
      navigate('/admin/courses');
    } catch {
      setSaveDraftToastError(
        t('courses.create.error_publishing_course') ||
          'Erro ao publicar curso. Tente novamente.'
      );
    } finally {
      setPublishSubmitting(false);
    }
  }, [
    currentStep,
    publishSubmitting,
    handleSaveDraft,
    resolvePublishedStatusId,
    updateCourseStatus,
    navigate,
    t,
  ]);

  const loadLessonForEdit = useCallback(
    async (lessonId: string): Promise<CourseLesson | null> => {
      try {
        const detail = await fetchModuleLesson(lessonId);
        return mapLessonDetailToCourseLesson(detail);
      } catch {
        return null;
      }
    },
    [fetchModuleLesson]
  );

  const handleNextStep = useCallback(() => {
    if (currentStep === 1) {
      const validation = validateStepOne(basicInfoData ?? null);
      if (!validation.valid || !basicInfoData) return;
      setStep1Error(null);
      setCurrentStep(2);
      return;
    }
    if (currentStep === 2) {
      if (!validateStepTwo(modulesData).valid) return;
      setCurrentStep(3);
      setStep3ReachedViaNext(true);
    } else if (currentStep < 3) {
      setCurrentStep(s => s + 1);
    }
  }, [currentStep, basicInfoData, modulesData]);

  const handleStepClick = useCallback(
    (step: number) => {
      // Permite voltar livremente; bloqueia avançar se passos anteriores estiverem inválidos.
      if (step === currentStep) return;

      if (step > currentStep) {
        if (step === 2) {
          const validation1 = validateStepOne(basicInfoData ?? null);
          if (!validation1.valid) return;
        }

        if (step === 3) {
          const validation1 = validateStepOne(basicInfoData ?? null);
          if (!validation1.valid) return;

          const validation2 = validateStepTwo(modulesData);
          if (!validation2.valid) return;
        }
      }

      setCurrentStep(step);
      if (step === 3) setStep3ReachedViaNext(false);
    },
    [currentStep, basicInfoData, modulesData]
  );

  const handlePreviousStep = useCallback(() => {
    if (currentStep > 1) setCurrentStep(s => s - 1);
  }, [currentStep]);

  const pageStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    padding: `${theme.spacing.xl} ${theme.spacing['2xl']}`,
    fontFamily: theme.typography.fontFamily,
    backgroundColor: theme.palette.background.default,
  };

  const layoutStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    gap: theme.spacing.xl,
    alignItems: 'stretch',
    width: '100%',
    minHeight: 0,
  };

  const contentContainerStyle: React.CSSProperties = {
    flex: 1,
    width: '100%',
    maxWidth: 1400,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
  };

  const mainColumnStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  };

  const mainStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.sm,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
  };

  return (
    <div style={pageStyle}>
      {saveDraftToastError && (
        <Toast message={saveDraftToastError} variant="error" role="alert" />
      )}
      <div style={contentContainerStyle}>
        <CreateCourseHeader
          backLink={
            <Link
              to="/admin/courses"
              style={{
                color: 'inherit',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <img
                src={iconArrowLeftHeaderCourses}
                alt=""
                width={18}
                height={18}
                aria-hidden
                style={{ display: 'block' }}
              />
              {t('courses.create.back_to_courses')}
            </Link>
          }
          onSaveDraft={isEditMode || currentStep === 3 ? handleSaveDraft : undefined}
          onPublish={currentStep === 3 ? handlePublish : undefined}
          saveDraftDisabled={
            saveDraftSubmitting ||
            publishSubmitting ||
            (!basicInfoData ? true : false) ||
            (isEditMode ? !audienceLoadedFromEdit || !modulesLoadedFromEdit : false)
          }
          publishDisabled={
            saveDraftSubmitting ||
            publishSubmitting ||
            (!basicInfoData ? true : false) ||
            (isEditMode ? !audienceLoadedFromEdit || !modulesLoadedFromEdit : false)
          }
          onNextStep={handleNextStep}
          nextStepDisabled={
            currentStep === 3 ||
            step1Submitting ||
            (currentStep === 1 && !isStep1Valid) ||
            (currentStep === 2 && !isStep2Valid)
          }
        />
        <div style={layoutStyle}>
          <CreateCourseSidebar
            currentStep={currentStep}
            completionPercent={completionPercent}
            step3ReachedViaNext={step3ReachedViaNext}
            onStepClick={handleStepClick}
          />
          <div style={mainColumnStyle}>
            <main style={mainStyle}>
              {currentStep === 1 && (
                <>
                  {step1LoadingEdit && (
                    <div
                      style={{
                        padding: theme.spacing.xl,
                        textAlign: 'center',
                        color: theme.palette.text.secondary,
                        fontSize: theme.typography.fontSize.sm,
                      }}
                    >
                      {t('courses.create.loading_course') ?? 'Carregando curso...'}
                    </div>
                  )}
                  {!step1LoadingEdit && step1Error && (
                    <div
                      role="alert"
                      style={{
                        padding: theme.spacing.md,
                        margin: theme.spacing.md,
                        backgroundColor: theme.palette.error?.main
                          ? `${theme.palette.error.main}20`
                          : '#fee',
                        color: theme.palette.error?.main ?? '#c00',
                        borderRadius: theme.shape.borderRadius.sm,
                        fontSize: theme.typography.fontSize.sm,
                      }}
                    >
                      {step1Error}
                    </div>
                  )}
                  {!step1LoadingEdit && (
                    <CreateCourseStepOne
                      categoryOptions={categoryOptions}
                      initialData={basicInfoData ?? undefined}
                      onChange={(data) => {
                        setBasicInfoData(data);
                        if (step1Error) setStep1Error(null);
                      }}
                      validationErrors={
                        currentStep === 1 && !isStep1Valid
                          ? step1Validation.errors
                          : undefined
                      }
                    />
                  )}
                </>
              )}
              {currentStep === 2 && (
                <CreateCourseStepTwo
                  key={isEditMode ? `edit-${courseIdParam ?? 'course'}` : 'new'}
                  initialModules={modulesData.length > 0 ? modulesData : undefined}
                  onChange={setModulesData}
                  sectionValidationErrorKey={step2Validation.sectionErrorKey}
                  moduleValidationErrors={step2Validation.moduleErrors}
                  loadLessonForEdit={loadLessonForEdit}
                  fetchQuestionsForQuiz={fetchQuestionsForQuiz}
                />
              )}
              {currentStep === 3 && (
                <>
                  <LoadingModal
                    open={saveDraftSubmitting}
                    message={t('courses.create.saving_draft')}
                  />
                  {!saveDraftSubmitting && (
                    <CreateCourseStepThree
                      key={
                        isEditMode
                          ? `step3-edit-${courseIdParam}-${audienceLoadedFromEdit}`
                          : 'step3-new'
                      }
                      modulesCount={modulesData.length}
                      lessonsCount={modulesData.reduce(
                        (acc, m) => acc + m.lessons.length,
                        0
                      )}
                      initialGroups={isEditMode ? initialTargetingFromEdit.groups : undefined}
                      initialUsers={isEditMode ? initialTargetingFromEdit.users : undefined}
                      fetchTargetingGroups={fetchTargetingGroups}
                      fetchTargetingUsers={fetchTargetingUsers}
                      onChangeTargeting={setTargetingSelection}
                    />
                  )}
                </>
              )}
            </main>
            <CreateCourseStepPagination
              currentStep={currentStep}
              totalSteps={3}
              onPreviousStep={handlePreviousStep}
              onNextStep={handleNextStep}
              nextStepDisabled={
                (currentStep === 1 && step1Submitting) ||
                (currentStep === 1 && !isStep1Valid) ||
                (currentStep === 2 && !isStep2Valid)
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
